/* =========================================================================
   DAM GOOD CO. — Motion Layer
   Wires up GSAP + Lenis + IntersectionObserver patterns.
   Load via: theme.liquid (or layout file) → before </body>
   Dependencies (CDN, also in head):
     <script src="https://cdn.jsdelivr.net/npm/lenis@1.1.13/dist/lenis.min.js" defer></script>
     <script src="https://cdn.jsdelivr.net/npm/gsap@3.13.0/dist/gsap.min.js" defer></script>
     <script src="https://cdn.jsdelivr.net/npm/gsap@3.13.0/dist/ScrollTrigger.min.js" defer></script>
   ========================================================================= */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ------------------------------------------------------------------
  // 1. Lenis smooth scroll (site-wide)
  // ------------------------------------------------------------------
  function initLenis() {
    if (reduceMotion || typeof window.Lenis === 'undefined') return null;
    var lenis = new window.Lenis({
      duration: 1.15,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: false,
      smoothTouch: false,
      touchMultiplier: 1.5
    });
    document.documentElement.classList.add('dg-lenis-active');
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    if (window.gsap && window.ScrollTrigger) {
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
      window.gsap.ticker.lagSmoothing(0);
    }
    window.__dgLenis = lenis;
    return lenis;
  }

  // ------------------------------------------------------------------
  // 2. Section reveal on scroll (IntersectionObserver)
  // ------------------------------------------------------------------
  function initReveal() {
    if (reduceMotion) {
      document.querySelectorAll('.dg-reveal').forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });
    document.querySelectorAll('.dg-reveal').forEach(function (el) { io.observe(el); });
  }

  // ------------------------------------------------------------------
  // 3. Char-by-char text reveal (hero / select headlines)
  //    Apply class "dg-text-split" to any element you want broken up.
  // ------------------------------------------------------------------
  function initTextSplit() {
    document.querySelectorAll('.dg-text-split').forEach(function (el) {
      if (el.dataset.dgSplit === 'done') return;
      el.dataset.dgSplit = 'done';
      var text = el.textContent.trim();
      el.textContent = '';
      var words = text.split(/(\s+)/);
      words.forEach(function (word) {
        if (/^\s+$/.test(word)) {
          el.appendChild(document.createTextNode(word));
          return;
        }
        var wordSpan = document.createElement('span');
        wordSpan.style.display = 'inline-block';
        wordSpan.style.whiteSpace = 'nowrap';
        word.split('').forEach(function (ch) {
          var s = document.createElement('span');
          s.className = 'dg-char';
          s.textContent = ch;
          wordSpan.appendChild(s);
        });
        el.appendChild(wordSpan);
      });
    });

    if (reduceMotion) {
      document.querySelectorAll('.dg-char').forEach(function (c) { c.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var chars = entry.target.querySelectorAll('.dg-char');
        chars.forEach(function (c, i) {
          setTimeout(function () { c.classList.add('is-in'); }, i * 28);
        });
        io.unobserve(entry.target);
      });
    }, { threshold: 0.5 });
    document.querySelectorAll('.dg-text-split').forEach(function (el) { io.observe(el); });
  }

  function initStickyHeader() {
    var header = document.querySelector('.header-wrapper, .header');
    if (!header) return;
    function onScroll() {
      var y = window.scrollY;
      if (y > 80) header.classList.add('dg-header--shrunk');
      else header.classList.remove('dg-header--shrunk');
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  function initMagnetic() {
    if (reduceMotion) return;
    document.querySelectorAll('.dg-magnetic').forEach(function (btn) {
      var bounds;
      btn.addEventListener('mouseenter', function () { bounds = btn.getBoundingClientRect(); });
      btn.addEventListener('mousemove', function (e) {
        if (!bounds) bounds = btn.getBoundingClientRect();
        var dx = e.clientX - (bounds.left + bounds.width / 2);
        var dy = e.clientY - (bounds.top + bounds.height / 2);
        btn.style.transform = 'translate(' + (dx*0.25) + 'px, ' + (dy*0.25) + 'px)';
      });
      btn.addEventListener('mouseleave', function () { btn.style.transform = ''; });
    });
  }

  function initMascotPeeks() {
    if (reduceMotion) {
      document.querySelectorAll('.dg-mascot-peek').forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) entry.target.classList.add('is-visible');
      });
    }, { threshold: 0.3 });
    document.querySelectorAll('.dg-mascot-peek').forEach(function (el) { io.observe(el); });
  }

  function initHeroParallax() {
    if (reduceMotion || !window.gsap || !window.ScrollTrigger) return;
    var hero = document.querySelector('[data-dg-parallax]');
    if (!hero) return;
    var back  = hero.querySelector('[data-layer="back"]');
    var mid   = hero.querySelector('[data-layer="mid"]');
    var front = hero.querySelector('[data-layer="front"]');
    if (back) window.gsap.to(back,  { yPercent: 18, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true }});
    if (mid)  window.gsap.to(mid,   { yPercent: 32, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true }});
    if (front)window.gsap.to(front, { yPercent: 50, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true }});
  }

  function initComicHorizontal() {
    if (reduceMotion || !window.gsap || !window.ScrollTrigger) return;
    document.querySelectorAll('[data-dg-horizontal-scroll]').forEach(function (section) {
      var track = section.querySelector('[data-track]');
      if (!track) return;
      var distance = track.scrollWidth - section.clientWidth;
      if (distance <= 0) return;
      window.gsap.to(track, { x: -distance, ease: 'none', scrollTrigger: { trigger: section, start: 'top top', end: function () { return '+=' + distance; }, pin: true, scrub: 0.8, anticipatePin: 1, invalidateOnRefresh: true }});
    });
  }

  function initStickyImageStack() {
    if (reduceMotion || !window.gsap || !window.ScrollTrigger) return;
    document.querySelectorAll('[data-dg-sticky-stack]').forEach(function (section) {
      var frames = section.querySelectorAll('[data-frame]');
      if (!frames.length) return;
      frames.forEach(function (frame, i) {
        if (i === 0) return;
        window.gsap.fromTo(frame, { opacity: 0, yPercent: 10 }, { opacity: 1, yPercent: 0, scrollTrigger: { trigger: frame, start: 'top 70%', end: 'top 30%', scrub: true }});
      });
    });
  }

  function boot() {
    initLenis(); initReveal(); initTextSplit(); initStickyHeader();
    initMagnetic(); initMascotPeeks(); initHeroParallax();
    initComicHorizontal(); initStickyImageStack();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
